import { computed, ref } from "vue";
import type { ComputedRef, InjectionKey, Ref } from "vue";
import type {
  AiGeneratePayload,
  AiGenerateTarget,
  AiManyGenerator,
  AiMode,
  AiSelectPayload,
  AiTransformAction,
  AiTransformPayload,
  StreamHandle,
} from "./types";

export interface AiCardFieldOptions {
  getValue: () => string;
  setValue: (value: string) => void;
  group?: "list";
  isDisabled?: () => boolean;
}

export interface AiCardFieldHandle {
  key: string;
  unregister: () => void;
}

export interface AiCardListOptions {
  getItems: () => string[];
  replace: (items: string[]) => void;
}

export interface AiCardListHandle {
  unregister: () => void;
}

export interface AiCardReview {
  mode: Ref<AiMode>;
  actionLabel: Ref<string>;
  showOriginal: Ref<boolean>;
  hasListFields: ComputedRef<boolean>;
  listReviewItems: ComputedRef<string[] | null>;
  register: (options: AiCardFieldOptions) => AiCardFieldHandle;
  registerList: (options: AiCardListOptions) => AiCardListHandle;
  run: (payload: AiSelectPayload) => void;
  retry: () => void;
  accept: () => void;
  cancel: () => void;
  toggleOriginal: () => void;
  reset: () => void;
  readonly: ComputedRef<boolean>;
  displayedValue: (key: string) => string | null;
  isChanged: (key: string) => boolean;
}

export const AiCardReviewKey: InjectionKey<AiCardReview> = Symbol("AiCardReview");

const TRANSFORM_LABELS: Record<AiTransformAction, string> = {
  correct: "Fixing spelling",
  rewrite: "Rewriting the card",
  translate: "Translating the card",
  custom: "Applying your request",
  emoji: "Adding emojis",
};

const GENERATE_LABELS: Record<AiGenerateTarget, string> = {
  answers: "Generating answers",
};

export const useAiCardReview = (generateMany: AiManyGenerator): AiCardReview => {
  const fields = ref(new Map<string, AiCardFieldOptions>());
  let nextKey = 0;

  const mode = ref<AiMode>("idle");
  const actionLabel = ref("");
  const showOriginal = ref(false);
  const originals = ref(new Map<string, string>());
  const pendings = ref(new Map<string, string>());
  const pendingListItems = ref<string[] | null>(null);
  const list = ref<AiCardListOptions | null>(null);
  const lastSelection = ref<AiSelectPayload | null>(null);
  let handle: StreamHandle | null = null;

  const readonly = computed(() => mode.value !== "idle");
  const hasListFields = computed(() => [...fields.value.values()].some((field) => field.group === "list"));
  const listReviewItems = computed(() =>
    mode.value === "reviewing" && !showOriginal.value ? pendingListItems.value : null,
  );

  const register = (options: AiCardFieldOptions): AiCardFieldHandle => {
    const key = `field-${nextKey++}`;
    fields.value.set(key, options);
    return { key, unregister: () => fields.value.delete(key) };
  };

  const registerList = (options: AiCardListOptions): AiCardListHandle => {
    list.value = options;
    return {
      unregister: () => {
        if (list.value === options) list.value = null;
      },
    };
  };

  const cancelStream = (): void => {
    handle?.cancel();
    handle = null;
  };

  const reset = (): void => {
    cancelStream();
    mode.value = "idle";
    actionLabel.value = "";
    showOriginal.value = false;
    originals.value = new Map();
    pendings.value = new Map();
    pendingListItems.value = null;
    lastSelection.value = null;
  };

  const targetsFor = (action: AiTransformAction): Map<string, AiCardFieldOptions> => {
    const targets = new Map<string, AiCardFieldOptions>();
    fields.value.forEach((field, key) => {
      if (field.isDisabled?.()) return;
      if (action === "emoji" && field.group !== "list") return;
      targets.set(key, field);
    });
    return targets;
  };

  const start = (payload: AiSelectPayload): void => {
    cancelStream();
    lastSelection.value = payload;
    actionLabel.value =
      payload.action === "generate" ? GENERATE_LABELS[payload.target] : TRANSFORM_LABELS[payload.action];
    showOriginal.value = false;
    pendings.value = new Map();
    originals.value = new Map();
    pendingListItems.value = null;
    mode.value = "generating";
  };

  const runGenerate = (payload: AiGeneratePayload): void => {
    const target = list.value;
    if (!target) return;

    start(payload);
    handle = generateMany(
      {
        action: "generate",
        target: payload.target,
        question: payload.question,
        items: target.getItems(),
      },
      (results) => {
        handle = null;
        if (results.length === 0) {
          reset();
          return;
        }
        pendingListItems.value = results.map(({ content }) => content);
        mode.value = "reviewing";
      },
    );
  };

  const runTransform = (payload: AiTransformPayload): void => {
    const targets = targetsFor(payload.action);
    if (targets.size === 0) return;

    start(payload);
    originals.value = new Map([...targets].map(([key, field]) => [key, field.getValue()]));

    handle = generateMany(
      {
        action: payload.action,
        lang: payload.lang,
        prompt: payload.prompt,
        contents: [...targets].map(([key, field]) => ({ key, content: field.getValue() })),
      },
      (results) => {
        handle = null;
        if (results.length === 0) {
          reset();
          return;
        }
        pendings.value = new Map(
          results.filter(({ key }) => originals.value.has(key)).map(({ key, content }) => [key, content]),
        );
        mode.value = "reviewing";
      },
    );
  };

  const run = (payload: AiSelectPayload): void => {
    if (payload.action === "generate") runGenerate(payload);
    else runTransform(payload);
  };

  const retry = (): void => {
    if (lastSelection.value) run(lastSelection.value);
  };

  const accept = (): void => {
    if (pendingListItems.value) list.value?.replace(pendingListItems.value);
    pendings.value.forEach((content, key) => {
      const field = fields.value.get(key);
      if (field && content !== originals.value.get(key)) field.setValue(content);
    });
    reset();
  };

  const cancel = (): void => reset();

  const toggleOriginal = (): void => {
    showOriginal.value = !showOriginal.value;
  };

  const displayedValue = (key: string): string | null => {
    if (mode.value !== "reviewing" || showOriginal.value) return null;
    return pendings.value.get(key) ?? null;
  };

  const isChanged = (key: string): boolean => {
    if (mode.value !== "reviewing") return false;
    const pending = pendings.value.get(key);
    return pending != null && pending !== originals.value.get(key);
  };

  return {
    mode,
    actionLabel,
    showOriginal,
    hasListFields,
    listReviewItems,
    register,
    registerList,
    run,
    retry,
    accept,
    cancel,
    toggleOriginal,
    reset,
    readonly,
    displayedValue,
    isChanged,
  };
};
